import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import type { SortingState } from "@tanstack/react-table"
import { getCoreRowModel, getSortedRowModel } from "@tanstack/react-table"
import type { FC } from "react"
import { useState } from "react"

import { useCreateAlternative } from "../../hooks/useCreateAlternative"
import type { AvailableAlternateItem } from "../../hooks/useGetAvailableAlternates"
import { useGetAvailableAlternates } from "../../hooks/useGetAvailableAlternates"

import { AlternativeProductSearchSuggestion } from "./AlternativeProductSearchSuggestion"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { Product } from "@/graphql/codegen/graphql"
import useAssociateProductColumns from "@/screens/Products/hooks/useAssociateProductColumns"
import type { AutocompleteOption } from "@/types/form-fields"

interface AddAlternativeDialogProps {
  companyProductAssociationLocationId: string
  productId?: string
  onClose: () => void
  onSuccess: () => void
}

export const AddAlternativeDialog: FC<AddAlternativeDialogProps> = ({
  companyProductAssociationLocationId,
  productId,
  onClose,
  onSuccess,
}) => {
  const [searchInputValue, setSearchInputValue] = useState("")
  const [submittedQuery, setSubmittedQuery] = useState("")
  const [selectedAlternate, setSelectedAlternate] = useState<AvailableAlternateItem | null>(null)
  const [sorting, setSorting] = useState<SortingState>([])

  const { availableAlternates, isFetching } = useGetAvailableAlternates({
    companyProductAssociationLocationId,
    searchTerm: submittedQuery,
    pause: submittedQuery.length < 2,
  })

  const { createAlternative, isCreating } = useCreateAlternative()

  const showSearchResults = submittedQuery.length >= 2

  // TODO: remove cast once BE fixes product field to be non-nullable (Product!)
  const tableData = availableAlternates.map((alt) => alt.product as Product)

  const selectedProductId = selectedAlternate?.product?.productId ?? null
  const rowSelection = selectedProductId !== null ? { [String(selectedProductId)]: true } : {}

  const handleSearch = (value: string | AutocompleteOption) => {
    const query = typeof value === "string" ? value : value.label
    setSubmittedQuery(query)
    setSelectedAlternate(null)
  }

  const handleSelect = (product: Product) => {
    const alternate = availableAlternates.find((alt) => alt.product?.productId === product.productId) ?? null
    setSelectedAlternate(alternate)
  }

  const handleSave = async () => {
    if (!selectedAlternate) return
    const success = await createAlternative({
      companyProductAssociationLocationId,
      alternateCompanyProductAssociationLocationId: selectedAlternate.alternateCompanyProductAssociationLocationId,
    })
    if (success) {
      onSuccess()
      onClose()
    }
  }

  const columns = useAssociateProductColumns({
    showRadioButton: true,
    onSelect: handleSelect,
  })

  return (
    <Dialog open onClose={onClose} maxWidth='lg' fullWidth>
      <DialogHeader title='Add Alternative Product' onClose={onClose} />
      <DialogContent classes={{ root: "p-10" }}>
        <Box className='flex flex-col gap-6'>
          <Typography className='text-gray-900'>
            {`Look for Products to Add as an Alternate to Product ID ${productId ?? ""}`}
          </Typography>

          <AlternativeProductSearchSuggestion
            companyProductAssociationLocationId={companyProductAssociationLocationId}
            searchInputValue={searchInputValue}
            onInputValueChange={setSearchInputValue}
            onSearch={handleSearch}
          />

          {showSearchResults && (
            <Box className='max-h-96 overflow-auto'>
              <AerosBaseTable
                features={{
                  tanstackOptions: {
                    data: tableData,
                    columns,
                    state: { isLoading: isFetching, rowSelection, sorting },
                    getCoreRowModel: getCoreRowModel(),
                    getSortedRowModel: getSortedRowModel(),
                    enableSorting: true,
                    enableMultiSort: false,
                    enableSortingRemoval: false,
                    onSortingChange: setSorting,
                    getRowId: (row) => String(row.productId),
                  },
                }}
                slotProps={{
                  bodyRow: { className: "cursor-pointer hover:bg-primary-100" },
                }}
                noDataMessage='No products found'
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      {showSearchResults && (
        <DialogActions className='justify-between p-10 pt-0'>
          <Button appearance='outlined' onClick={onClose}>
            Cancel
          </Button>
          <Button appearance='contained' onClick={handleSave} disabled={!selectedAlternate || isCreating}>
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
